import { useEffect, useMemo, useState } from "react";
import { AppShell } from "../components/AppShell";
import { api } from "../lib/apiClient";
import {
  AlertTriangle, Loader2, Trophy, Clock, Info, TrendingUp,
  ListFilter, Activity, History, Swords, Target, MapPin, Ban,
  Newspaper, Sparkles, ChevronLeft, ChevronRight,
} from "lucide-react";

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("fr-FR", {
      hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris",
    });
  } catch { return "--:--"; }
};
const fmtDateLong = (ymd) => {
  try {
    return new Date(ymd + "T12:00:00").toLocaleDateString("fr-FR", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  } catch { return ymd; }
};
const fmtDateShort = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return "—"; }
};
const odd = (v) => (typeof v === "number" ? v.toFixed(2) : "—");
const pct = (v) => (typeof v === "number" ? `${v.toFixed(1)} %` : "—");
const NA = <span className="font-mono-hud text-[10px] uppercase tracking-wider text-[#64748b]">données indisponibles</span>;

const FORM_COLORS = { W: "#34d399", N: "#f59e0b", D: "#f59e0b", L: "#f87171" };
function FormBadges({ form }) {
  if (!form) return NA;
  return (
    <div className="flex flex-wrap gap-1">
      {form.slice(-6).split("").map((c, i) => (
        <span key={i} className="flex h-5 w-5 items-center justify-center rounded text-[10px] font-bold text-black"
          style={{ background: FORM_COLORS[c] || "#475569" }}>{c}</span>
      ))}
    </div>
  );
}

function Warning() {
  return (
    <div data-testid="football-warning" className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-amber-200">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <p className="text-xs leading-snug">
        Les probabilités et <strong>cotes justes</strong> sont des <strong>estimations</strong> d'un modèle statistique (Poisson), pas des prédictions certaines.
        Aucun pari n'est garanti — ne considérez aucune mise comme un gain assuré.
      </p>
    </div>
  );
}

const panel = "rounded-xl border border-[var(--border-hud)] bg-[rgba(4,10,22,0.6)] p-4 backdrop-blur-md";
const PanelTitle = ({ icon: Icon, color, children }) => (
  <h3 className="mb-2 flex items-center gap-2 font-mono-hud text-[11px] uppercase tracking-wider text-white">
    <Icon className="h-4 w-4" style={color ? { color } : undefined} /> {children}
  </h3>
);
const Dt = ({ label, children }) => (
  <div className="flex items-center justify-between gap-3 border-b border-white/5 py-1.5 last:border-0">
    <span className="text-[11px] text-[#8b949e]">{label}</span>
    <span className="text-right text-xs font-semibold text-white">{children}</span>
  </div>
);

function TeamStats({ s, side }) {
  if (!s?.available) return <div className={panel}><p className="text-xs text-[#8b949e]">{NA}</p></div>;
  const v = (o) => (o && o.total) ?? null;
  const gfa = s.goals_for_avg || {}, gaa = s.goals_against_avg || {};
  return (
    <div className={panel} data-testid={`football-teamstats-${side}`}>
      <div className="mb-2 flex items-center gap-2">
        {s.team?.logo && <img src={s.team.logo} alt="" className="h-6 w-6 object-contain" />}
        <h4 className="font-mono-hud text-[11px] uppercase tracking-wider text-white">
          {s.team?.name} <span className="text-[var(--accent)]">· {side === "home" ? "domicile" : "extérieur"}</span>
        </h4>
      </div>
      <p className="mb-2 text-[10px] text-[#64748b]">Classement saison — split domicile/extérieur non fourni par les sources gratuites.</p>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] text-[#8b949e]">Forme (classement)</span>
        <FormBadges form={s.form} />
      </div>
      <Dt label="Classement">{s.position ? `${s.position}ᵉ` : "—"}{typeof s.points === "number" ? ` · ${s.points} pts` : ""}</Dt>
      <Dt label="Matchs joués">{v(s.played) ?? "—"}</Dt>
      <Dt label="V / N / D">{v(s.wins) ?? "—"} / {v(s.draws) ?? "—"} / {v(s.loses) ?? "—"}</Dt>
      <Dt label="Buts marqués (moy.)">{v(gfa) ?? NA}</Dt>
      <Dt label="Buts encaissés (moy.)">{v(gaa) ?? NA}</Dt>
      <Dt label="Buts marqués (total)">{v(s.goals_for_total) ?? "—"}</Dt>
      <Dt label="Buts encaissés (total)">{v(s.goals_against_total) ?? "—"}</Dt>
    </div>
  );
}

function MarketTable({ title, rows }) {
  if (!rows?.length) return null;
  return (
    <div className="mb-4 last:mb-0">
      <h4 className="mb-1.5 font-mono-hud text-[10px] uppercase tracking-[0.15em] text-[var(--accent)]">{title}</h4>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[280px] border-collapse text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#64748b]">
              <th className="py-1.5 pr-2 font-medium">Résultat</th>
              <th className="py-1.5 px-2 font-medium">Probabilité</th>
              <th className="py-1.5 pl-2 font-medium text-[var(--accent)]">Cote juste<br /><span className="normal-case text-[9px] text-[#64748b]">1 / proba</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-white/5">
                <td className="py-1.5 pr-2 text-white">{r.sel}</td>
                <td className="py-1.5 px-2 text-[#cbd5e1]">{pct(r.prob_pct)}</td>
                <td className="py-1.5 pl-2 font-semibold text-[var(--accent)]">{odd(r.fair)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RecentForm({ r, name }) {
  if (!r?.available) return (
    <div className={panel}>
      <h4 className="mb-1 font-mono-hud text-[11px] uppercase tracking-wider text-white">{name}</h4>
      <p className="text-xs text-[#8b949e]">{NA}</p>
    </div>
  );
  return (
    <div className={panel}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className="font-mono-hud text-[11px] uppercase tracking-wider text-white">{name}</h4>
        <FormBadges form={r.form} />
      </div>
      <div className="mb-2 grid grid-cols-3 gap-2 text-center">
        <div><p className="text-sm font-bold text-white">{r.played}</p><p className="text-[9px] uppercase tracking-wider text-[#64748b]">Matchs</p></div>
        <div><p className="text-sm font-bold text-white">{r.wins}-{r.draws}-{r.loses}</p><p className="text-[9px] uppercase tracking-wider text-[#64748b]">V-N-D</p></div>
        <div><p className="text-sm font-bold text-white">{r.avg_for} / {r.avg_against}</p><p className="text-[9px] uppercase tracking-wider text-[#64748b]">Buts m./e.</p></div>
      </div>
      <div className="space-y-1">
        {(r.matches || []).slice(0, 5).map((m, i) => (
          <div key={i} className="flex items-center justify-between gap-2 border-b border-white/5 py-1 text-[11px] last:border-0">
            <span className="flex items-center gap-1.5 truncate">
              <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-[9px] font-bold text-black" style={{ background: FORM_COLORS[m.result] || "#475569" }}>{m.result}</span>
              <span className="truncate text-[#cbd5e1]">{m.home ? "vs" : "@"} {m.opponent}</span>
            </span>
            <span className="shrink-0 font-mono-hud text-white">{m.score}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function H2H({ h }) {
  if (!h?.available) return null;
  return (
    <div className={panel} data-testid="football-h2h">
      <PanelTitle icon={Swords} color="var(--accent)">Confrontations directes</PanelTitle>
      <div className="mb-3 grid grid-cols-4 gap-2 text-center">
        <div><p className="text-base font-bold text-emerald-400">{h.home_wins ?? "—"}</p><p className="text-[9px] uppercase tracking-wider text-[#64748b]">{h.home_name}</p></div>
        <div><p className="text-base font-bold text-amber-400">{h.draws ?? "—"}</p><p className="text-[9px] uppercase tracking-wider text-[#64748b]">Nuls</p></div>
        <div><p className="text-base font-bold text-red-400">{h.away_wins ?? "—"}</p><p className="text-[9px] uppercase tracking-wider text-[#64748b]">{h.away_name}</p></div>
        <div><p className="text-base font-bold text-white">{h.total ?? "—"}</p><p className="text-[9px] uppercase tracking-wider text-[#64748b]">Matchs</p></div>
      </div>
      <div className="space-y-1">
        {(h.matches || []).map((m, i) => (
          <div key={i} className="flex items-center justify-between gap-2 border-b border-white/5 py-1 text-[11px] last:border-0">
            <span className="truncate text-[#8b949e]">{fmtDateShort(m.date)}</span>
            <span className="truncate text-[#cbd5e1]">{m.home} <span className="font-mono-hud text-white">{m.score}</span> {m.away}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ScorerCol({ title, list }) {
  return (
    <div>
      <p className="mb-1 font-mono-hud text-[10px] uppercase tracking-wider text-[var(--accent)]">{title}</p>
      {list?.length ? (
        <ul className="space-y-1">
          {list.map((p, i) => (
            <li key={i} className="flex items-center justify-between gap-2 text-[11px]">
              <span className="truncate text-[#cbd5e1]">{p.name}</span>
              <span className="shrink-0 font-mono-hud text-white">{p.goals ?? 0} ⚽{typeof p.assists === "number" ? ` · ${p.assists} 🅰` : ""}</span>
            </li>
          ))}
        </ul>
      ) : <p className="text-[11px] text-[#64748b]">—</p>}
    </div>
  );
}

function Identity({ e }) {
  if (!e?.available) return null;
  return (
    <span className="flex items-center gap-2 rounded-lg border border-[var(--border-hud)] bg-[rgba(4,10,22,0.5)] px-2.5 py-1.5">
      {e.badge && <img src={e.badge} alt="" className="h-6 w-6 object-contain" />}
      <span className="text-[10px] leading-tight text-[#8b949e]">
        <span className="block font-semibold text-white">{e.name}</span>
        {e.stadium && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{e.stadium}</span>}
        {e.formed && <span>Depuis {e.formed}</span>}
      </span>
    </span>
  );
}

function PronosticCard({ p, disclaimer, engine }) {
  if (!p || !p.recommandation) return null;
  const conf = p.confiance || "";
  const confColor = /élev/i.test(conf) ? "#34d399" : /moy/i.test(conf) ? "#f59e0b" : "#f87171";
  return (
    <div data-testid="football-pronostic" className="rounded-xl border-2 border-[var(--accent)]/50 bg-[var(--accent-a10)] p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Sparkles className="h-4 w-4 text-[var(--accent)]" />
        <h4 className="font-mono-hud text-[11px] uppercase tracking-wider text-[var(--accent)]">Pronostic de Sara</h4>
        {conf && <span className="ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold text-black" style={{ background: confColor }}>Confiance {conf}</span>}
      </div>
      {p.pari && (
        <div className="mb-2 flex items-center gap-2 rounded-lg bg-[var(--accent-a15)] px-3 py-2">
          <Target className="h-4 w-4 shrink-0 text-[var(--accent)]" />
          <span className="font-mono-hud text-[10px] uppercase tracking-wider text-[#8b949e]">Parier sur</span>
          <span data-testid="football-pronostic-pari" className="ml-auto text-right text-sm font-bold text-[var(--accent)]">{p.pari}</span>
        </div>
      )}
      <p data-testid="football-pronostic-reco" className="text-sm font-bold text-white">{p.recommandation}</p>
      {p.score_probable && <p className="mt-1 text-xs text-[#cbd5e1]">Score probable : <span className="font-mono-hud text-white">{p.score_probable}</span></p>}
      {p.analyse && <p className="mt-2 text-xs leading-relaxed text-[#cbd5e1]">{p.analyse}</p>}
      {disclaimer && <p className="mt-3 text-[10px] leading-snug text-[#64748b]">{disclaimer}</p>}
      {engine && <p className="mt-1 text-[9px] uppercase tracking-wider text-[#475569]">Rédigé par {engine}</p>}
    </div>
  );
}

function JournalColumn({ name, text, side }) {
  return (
    <div className={panel} data-testid={`football-journal-${side}`}>
      <h4 className="mb-2 font-mono-hud text-[11px] uppercase tracking-wider text-white">{name}</h4>
      <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-[#cbd5e1]">{text || NA}</p>
    </div>
  );
}

function JournalSection({ match }) {
  const [state, setState] = useState({ loading: false, data: null, error: null, started: false });
  const generate = () => {
    setState({ loading: true, data: null, error: null, started: true });
    api.get("/football/journal", {
      params: {
        fixture: match.fixture_id,
        code: match.league.code,
        home: match.home.id,
        away: match.away.id,
        home_name: match.home.name_full || match.home.name,
        away_name: match.away.name_full || match.away.name,
      },
    })
      .then((r) => setState({ loading: false, data: r.data, error: null, started: true }))
      .catch((e) => setState({ loading: false, data: null, started: true, error: e?.response?.data?.detail || "Journal indisponible." }));
  };
  const d = state.data;
  return (
    <div className={panel} data-testid="football-journal-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <PanelTitle icon={Newspaper} color="var(--accent)">Journal de bord IA — par Sara</PanelTitle>
        <button data-testid="football-journal-generate" onClick={generate} disabled={state.loading}
          className="flex items-center gap-1.5 rounded-lg border border-[var(--accent)]/50 bg-[var(--accent-a15)] px-3 py-1.5 font-mono-hud text-[10px] uppercase tracking-wider text-white transition-colors hover:bg-[var(--accent-a10)] disabled:opacity-50">
          {state.loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {d?.available ? "Régénérer" : "Générer"}
        </button>
      </div>
      {!state.started && (
        <p className="text-xs leading-snug text-[#8b949e]">
          Sara rédige un bilan factuel de chaque équipe (forme, stats, buteurs — blessures non disponibles en gratuit)
          et vous donne son <strong className="text-[var(--accent)]">pronostic</strong> sur le match.
        </p>
      )}
      {state.loading && (
        <div className="flex items-center gap-2 py-3 text-sm text-[#8b949e]">
          <Loader2 className="h-4 w-4 animate-spin" /> Sara rédige le journal de bord…
        </div>
      )}
      {state.error && <p className="text-xs text-amber-300">{state.error}</p>}
      {d && d.available === false && <p className="text-xs text-amber-300">{d.message || "Journal indisponible."}</p>}
      {d && d.available && (
        <div className="mt-3 space-y-4">
          <PronosticCard p={d.pronostic} disclaimer={d.disclaimer} engine={d.engine} />
          <div className="grid gap-4 sm:grid-cols-2">
            <JournalColumn side="home" name={d.home_name || match.home.name} text={d.journal_home} />
            <JournalColumn side="away" name={d.away_name || match.away.name} text={d.journal_away} />
          </div>
        </div>
      )}
    </div>
  );
}

function Analysis({ match, data, loading }) {
  if (loading) {
    return <div className={`${panel} flex items-center justify-center gap-2 text-sm text-[#8b949e]`}>
      <Loader2 className="h-4 w-4 animate-spin" /> Analyse en cours…
    </div>;
  }
  if (!data) return null;
  if (data.available === false) {
    return <div className={panel}><p className="text-sm text-amber-300">{data.message || "Analyse indisponible."}</p></div>;
  }
  const model = data.model || {};
  const c = data.comparison || {};
  const conf = model.available ? model.confidence : null;
  const sc = data.scorers || {};
  return (
    <div data-testid="football-analysis-panel" className="space-y-4">
      {/* header */}
      <div className={panel}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {match.home.logo && <img src={match.home.logo} alt="" className="h-8 w-8 object-contain" />}
            <span className="text-sm font-bold text-white">{match.home.name}</span>
            <span className="text-[#64748b]">vs</span>
            <span className="text-sm font-bold text-white">{match.away.name}</span>
            {match.away.logo && <img src={match.away.logo} alt="" className="h-8 w-8 object-contain" />}
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[#8b949e]">
          <span className="flex items-center gap-1"><Trophy className="h-3.5 w-3.5" />{match.league.name}{match.league.round ? ` · ${match.league.round}` : ""}</span>
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{fmtTime(match.date)} (Paris)</span>
          {conf && <span className="rounded-full border border-[var(--accent)]/40 bg-[var(--accent-a10)] px-2 py-0.5 font-mono-hud text-[10px] uppercase tracking-wider text-[var(--accent)]">Confiance : {conf}</span>}
          {model.available && <span className="text-[10px] text-[#64748b]">λ dom {model.lambda_home} · λ ext {model.lambda_away}</span>}
        </div>
      </div>

      <Warning />

      {/* AI journalist — Journal de bord + pronostic de Sara */}
      <JournalSection match={match} />

      {/* team identity chips */}
      {(data.team_extra_home?.available || data.team_extra_away?.available) && (
        <div className="flex flex-wrap gap-2">
          <Identity e={data.team_extra_home} />
          <Identity e={data.team_extra_away} />
        </div>
      )}

      {/* season stats */}
      <div className="grid gap-4 sm:grid-cols-2">
        <TeamStats s={data.home_stats} side="home" />
        <TeamStats s={data.away_stats} side="away" />
      </div>

      {/* model probabilities */}
      <div data-testid="football-model-table" className={panel}>
        <PanelTitle icon={TrendingUp} color="var(--accent)">Probabilités & cotes justes (modèle Poisson)</PanelTitle>
        <p className="mb-3 text-[10px] leading-snug text-[#64748b]">
          <span className="text-[var(--accent)]">Cote juste</span> = estimation du modèle (1 / probabilité).
          Aucune cote de bookmaker n'est affichée (données non gratuites). Ces estimations <strong>ne garantissent aucun gain</strong>.
        </p>
        {model.available ? (
          <>
            <p className="mb-3 text-[10px] text-[#64748b]">Échantillon : {model.games_sample} match(s) joués min. par équipe.</p>
            <MarketTable title="Résultat (1X2)" rows={c["1x2"]} />
            <MarketTable title="Double chance" rows={c["double_chance"]} />
            <MarketTable title="Plus / Moins de buts" rows={c["over_under"]} />
            <MarketTable title="Les deux équipes marquent" rows={c["btts"]} />
            <MarketTable title="Scores exacts les plus probables" rows={c["correct_score"]} />
          </>
        ) : (
          <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-[11px] leading-snug text-amber-200">
            Modèle indisponible{model.reason ? ` — ${model.reason}` : ""}. Le classement de la saison en cours est introuvable pour cette compétition.
          </p>
        )}
      </div>

      {/* recent form */}
      <div data-testid="football-recent">
        <PanelTitle icon={History} color="var(--accent)">Forme récente (derniers matchs)</PanelTitle>
        <div className="grid gap-4 sm:grid-cols-2">
          <RecentForm r={data.recent_home} name={match.home.name} />
          <RecentForm r={data.recent_away} name={match.away.name} />
        </div>
      </div>

      {/* head to head */}
      <H2H h={data.h2h} />

      {/* scorers */}
      {sc.available && (
        <div className={panel} data-testid="football-scorers">
          <PanelTitle icon={Target} color="var(--accent)">Meilleurs buteurs (compétition)</PanelTitle>
          <div className="grid gap-4 sm:grid-cols-2">
            <ScorerCol title={match.home.name} list={sc.home} />
            <ScorerCol title={match.away.name} list={sc.away} />
          </div>
        </div>
      )}

      {/* goal detail (OpenLigaDB) */}
      {data.goals_detail?.available && (
        <div className={panel} data-testid="football-goals-detail">
          <PanelTitle icon={Activity} color="var(--accent)">Buts de la dernière confrontation (détail)</PanelTitle>
          <ul className="space-y-1 text-[11px]">
            {data.goals_detail.goals.map((g, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="font-mono-hud text-[var(--accent)]">{g.minute}'</span>
                <span className="text-white">{g.player}</span>
                <span className="text-[#64748b]">{g.score}</span>
                {g.penalty && <span className="rounded border border-amber-500/30 px-1 text-[9px] text-amber-300">pén.</span>}
                {g.owngoal && <span className="rounded border border-red-500/30 px-1 text-[9px] text-red-300">csc</span>}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[10px] text-[#64748b]">Source : {data.goals_detail.source}</p>
        </div>
      )}

      {/* factors */}
      <div className={panel}>
        <PanelTitle icon={Info} color="var(--accent)">Facteurs pris en compte</PanelTitle>
        <ul className="space-y-1 text-xs text-[#cbd5e1]">
          {(data.factors || []).map((f, i) => (
            <li key={i} className="flex gap-2"><span className="text-[var(--accent)]">›</span>{f}</li>
          ))}
        </ul>
      </div>

      {/* unavailable */}
      {data.unavailable?.length > 0 && (
        <div className={panel}>
          <PanelTitle icon={Ban} color="#f59e0b">Données non fournies par les sources gratuites</PanelTitle>
          <div className="flex flex-wrap gap-2">
            {data.unavailable.map((u, i) => (
              <span key={i} className="rounded border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] text-[#8b949e]">{u}</span>
            ))}
          </div>
        </div>
      )}

      {/* sources */}
      <div className={panel}>
        <h3 className="mb-2 font-mono-hud text-[10px] uppercase tracking-wider text-[#8b949e]">Sources & mise à jour</h3>
        <div className="space-y-1 text-[10px] text-[#64748b]">
          <p>Stats & classements : {data.sources?.stats} · {data.stats_fetched_at ? new Date(data.stats_fetched_at).toLocaleString("fr-FR", { timeZone: "Europe/Paris" }) : "—"}</p>
          <p>Forme récente : {data.sources?.recent}</p>
          <p>Confrontations : {data.sources?.h2h}</p>
          <p>Buteurs : {data.sources?.scorers}{data.sources?.goals ? ` · détail : ${data.sources.goals}` : ""}</p>
          <p>Identité d'équipe : {data.sources?.identity}</p>
          {data.quota_exceeded && <p className="text-amber-400">Quota de la source gratuite atteint — certaines données peuvent manquer, réessayez dans une minute.</p>}
        </div>
        {data.disclaimer && <p className="mt-2 text-[10px] leading-snug text-[#64748b]">{data.disclaimer}</p>}
      </div>
    </div>
  );
}

export default function Football() {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [analysis, setAnalysis] = useState({ loading: false, data: null });
  const [date, setDate] = useState(null); // null = aujourd'hui (heure de Paris, côté serveur)

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    setSelected(null);
    setAnalysis({ loading: false, data: null });
    api.get("/football/matches", { params: date ? { date } : {} })
      .then((r) => { if (alive) setState({ loading: false, data: r.data, error: null }); })
      .catch((e) => { if (alive) setState({ loading: false, data: null, error: e?.message || "Erreur" }); });
    return () => { alive = false; };
  }, [date]);

  const matches = useMemo(() => state.data?.matches || [], [state.data]);
  const visible = useMemo(
    () => (filter === "featured" ? matches.filter((m) => m.featured) : matches),
    [matches, filter]
  );
  const grouped = useMemo(() => {
    const g = {};
    for (const m of visible) {
      const k = `${m.league.country || ""} · ${m.league.name}`;
      (g[k] = g[k] || []).push(m);
    }
    return g;
  }, [visible]);

  const selectMatch = (m) => {
    setSelected(m);
    setAnalysis({ loading: true, data: null });
    api.get("/football/analysis", {
      params: {
        fixture: m.fixture_id,
        code: m.league.code,
        home: m.home.id,
        away: m.away.id,
        home_name: m.home.name_full || m.home.name,
        away_name: m.away.name_full || m.away.name,
      },
    })
      .then((r) => setAnalysis({ loading: false, data: r.data }))
      .catch((e) => setAnalysis({ loading: false, data: { available: false, message: e?.response?.data?.detail || "Analyse indisponible." } }));
  };

  const noKey = state.data && state.data.available === false && state.data.reason === "no_key";
  const quota = state.data?.quota_exceeded || state.data?.api_error;
  const apiMsg = state.data?.message;

  const todayYMD = () => {
    try { return new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Paris" }); }
    catch { return new Date().toISOString().slice(0, 10); }
  };
  const curDate = state.data?.date || date || todayYMD();
  const isToday = curDate === todayYMD();
  const shiftDate = (delta) => {
    const d = new Date(curDate + "T12:00:00");
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0, 10));
  };

  return (
    <AppShell title="Football — Analyse & Pronostics" subtitle="Sources 100 % gratuites · estimations statistiques · pas de pari garanti" wide>
      <div data-testid="football-page" className="grid gap-6 grid-cols-[minmax(0,1fr)] xl:grid-cols-[minmax(0,370px)_minmax(0,1fr)]">
        {/* list column */}
        <div className="min-w-0 space-y-3">
          <Warning />
          <div data-testid="football-date-nav" className="flex items-center justify-between gap-2">
            <button data-testid="football-date-prev" onClick={() => shiftDate(-1)} aria-label="Jour précédent"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[var(--border-hud)] text-[#8b949e] transition-colors hover:border-[var(--accent)]/60 hover:text-white">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="min-w-0 flex-1 text-center">
              <p className="truncate font-mono-hud text-[11px] uppercase tracking-wider text-white">{fmtDateLong(curDate)}</p>
              {!isToday && (
                <button data-testid="football-date-today" onClick={() => setDate(null)}
                  className="mt-0.5 font-mono-hud text-[9px] uppercase tracking-wider text-[var(--accent)] hover:underline">
                  Revenir à aujourd'hui
                </button>
              )}
            </div>
            <button data-testid="football-date-next" onClick={() => shiftDate(1)} aria-label="Jour suivant"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[var(--border-hud)] text-[#8b949e] transition-colors hover:border-[var(--accent)]/60 hover:text-white">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="flex gap-2">
            <button data-testid="football-filter-all" onClick={() => setFilter("all")}
              className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 font-mono-hud text-[10px] uppercase tracking-wider transition-colors ${filter === "all" ? "border-[var(--accent)] bg-[var(--accent-a15)] text-white" : "border-[var(--border-hud)] text-[#8b949e] hover:text-white"}`}>
              <ListFilter className="h-3.5 w-3.5" /> Tous
            </button>
            <button data-testid="football-filter-featured" onClick={() => setFilter("featured")}
              className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 font-mono-hud text-[10px] uppercase tracking-wider transition-colors ${filter === "featured" ? "border-[var(--accent)] bg-[var(--accent-a15)] text-white" : "border-[var(--border-hud)] text-[#8b949e] hover:text-white"}`}>
              <Trophy className="h-3.5 w-3.5" /> Grands championnats
            </button>
          </div>

          {state.loading && <div className="flex items-center gap-2 py-8 text-sm text-[#8b949e]"><Loader2 className="h-4 w-4 animate-spin" /> Chargement des matchs…</div>}
          {noKey && <div className={panel}><p className="text-xs text-amber-300">{apiMsg || "Source de données Football non configurée."}</p></div>}
          {quota && <div className={panel}><p className="text-xs text-amber-300">{apiMsg || "Quota de la source gratuite atteint. Réessayez dans une minute."}</p></div>}
          {!state.loading && !noKey && !quota && visible.length === 0 && (
            <div className={panel}><p className="text-xs text-[#8b949e]">Aucun match {filter === "featured" ? "des grands championnats " : ""}pour cette date — données indisponibles.</p></div>
          )}

          <div className="space-y-4">
            {Object.entries(grouped).map(([league, ms]) => (
              <div key={league}>
                <p className="mb-1.5 flex items-center gap-1.5 font-mono-hud text-[10px] uppercase tracking-wider text-[#64748b]">
                  {ms[0].league.logo && <img src={ms[0].league.logo} alt="" className="h-4 w-4 object-contain" />}{league}
                </p>
                <div className="space-y-1.5">
                  {ms.map((m) => (
                    <button key={m.fixture_id} data-testid={`football-match-${m.fixture_id}`} onClick={() => selectMatch(m)}
                      className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${selected?.fixture_id === m.fixture_id ? "border-[var(--accent)] bg-[var(--accent-a15)]" : "border-[var(--border-hud)] bg-[rgba(4,10,22,0.5)] hover:border-[var(--accent)]/50"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                          {m.home.logo && <img src={m.home.logo} alt="" className="h-5 w-5 shrink-0 object-contain" />}
                          <span className="truncate text-xs font-semibold text-white">{m.home.name}</span>
                        </div>
                        <span className="shrink-0 font-mono-hud text-[10px] text-[var(--accent)]">
                          {m.finished && m.score ? m.score : fmtTime(m.date)}
                        </span>
                        <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
                          <span className="truncate text-right text-xs font-semibold text-white">{m.away.name}</span>
                          {m.away.logo && <img src={m.away.logo} alt="" className="h-5 w-5 shrink-0 object-contain" />}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* analysis column */}
        <div className="min-w-0">
          {selected ? (
            <Analysis match={selected} data={analysis.data} loading={analysis.loading} />
          ) : (
            <div className={`${panel} flex min-h-[200px] items-center justify-center`}>
              <p className="text-center text-sm text-[#8b949e]">Sélectionnez un match pour voir l'analyse détaillée :<br />stats, forme, confrontations, buteurs et probabilités du modèle.</p>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
